import type { CreateLeadInput } from '../../services/leadSyncService';

/**
 * CSV aus Excel, Google Sheets oder LinkedIn-Exporten in Lead-Eingaben.
 * Trennzeichen wird an der Kopfzeile erkannt, Spalten werden ueber
 * deutsche und englische Namen zugeordnet. Bewusst einfach: Anfuehrungs-
 * zeichen innerhalb einer Zeile ja, Zeilenumbrueche in Feldern nein.
 */

export interface CsvErgebnis {
  leads: CreateLeadInput[];
  /** Datenzeilen ohne Kopfzeile */
  zeilen: number;
  ohneEmail: number;
  spalten: string[];
  /** Kopfzeilen-Spalten, die keinem Feld zugeordnet wurden */
  unbekannt: string[];
  trennzeichen: string;
}

type Feld = keyof CreateLeadInput;

const ALIASE: Record<string, Feld> = {
  email: 'email', 'e-mail': 'email', mail: 'email', emailaddress: 'email', 'e-mail-adresse': 'email',
  firstname: 'firstName', 'first name': 'firstName', first_name: 'firstName', vorname: 'firstName',
  lastname: 'lastName', 'last name': 'lastName', last_name: 'lastName', nachname: 'lastName', name: 'lastName',
  company: 'company', firma: 'company', unternehmen: 'company', organisation: 'company', organization: 'company',
  jobtitle: 'jobTitle', 'job title': 'jobTitle', job_title: 'jobTitle', position: 'jobTitle', titel: 'jobTitle', title: 'jobTitle', rolle: 'jobTitle',
  phone: 'phone', telefon: 'phone', tel: 'phone', mobile: 'phone', mobil: 'phone',
  linkedinurl: 'linkedInUrl', linkedin: 'linkedInUrl', 'linkedin url': 'linkedInUrl', linkedin_url: 'linkedInUrl', profil: 'linkedInUrl',
  website: 'website', web: 'website', url: 'website', domain: 'website', homepage: 'website',
  location: 'location', ort: 'location', stadt: 'location', city: 'location', standort: 'location',
  notes: 'notes', notizen: 'notes', notiz: 'notes', bemerkung: 'notes',
  source: 'source', quelle: 'source',
};

const normalisiere = (s: string) => s.trim().replace(/^\uFEFF/, '').replace(/^"|"$/g, '').toLowerCase().replace(/\s+/g, ' ');

export const erkenneTrennzeichen = (kopf: string): string => {
  const kandidaten = [';', ',', '\t', '|'];
  let bestes = ',';
  let max = -1;
  kandidaten.forEach((t) => {
    const n = kopf.split(t).length - 1;
    if (n > max) { max = n; bestes = t; }
  });
  return bestes;
};

/** Eine Zeile in Felder zerlegen, Anfuehrungszeichen mit "" als Escape. */
export const zerlegeZeile = (zeile: string, trennzeichen: string): string[] => {
  const felder: string[] = [];
  let aktuell = '';
  let inAnfuehrung = false;
  for (let i = 0; i < zeile.length; i += 1) {
    const c = zeile[i];
    if (inAnfuehrung) {
      if (c === '"') {
        if (zeile[i + 1] === '"') { aktuell += '"'; i += 1; } else inAnfuehrung = false;
      } else aktuell += c;
    } else if (c === '"') {
      inAnfuehrung = true;
    } else if (c === trennzeichen) {
      felder.push(aktuell);
      aktuell = '';
    } else {
      aktuell += c;
    }
  }
  felder.push(aktuell);
  return felder.map((f) => f.trim());
};

const gueltigeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export function parseLeadCsv(text: string, quelle = 'csv'): CsvErgebnis {
  const zeilen = text.replace(/\r\n?/g, '\n').split('\n').filter((z) => z.trim().length > 0);
  if (zeilen.length === 0) {
    return { leads: [], zeilen: 0, ohneEmail: 0, spalten: [], unbekannt: [], trennzeichen: ',' };
  }
  const trennzeichen = erkenneTrennzeichen(zeilen[0]);
  const kopf = zerlegeZeile(zeilen[0], trennzeichen).map(normalisiere);
  const zuordnung = kopf.map((name) => ALIASE[name] ?? null);
  const unbekannt = kopf.filter((name, i) => !zuordnung[i] && name);

  const leads: CreateLeadInput[] = [];
  let ohneEmail = 0;
  zeilen.slice(1).forEach((zeile) => {
    const werte = zerlegeZeile(zeile, trennzeichen);
    const lead: Partial<CreateLeadInput> = {};
    zuordnung.forEach((feld, i) => {
      const wert = werte[i]?.trim();
      if (!feld || !wert) return;
      (lead as Record<string, string>)[feld] = wert;
    });
    if (!lead.email || !gueltigeEmail(lead.email)) {
      ohneEmail += 1;
      return;
    }
    leads.push({ ...lead, email: lead.email.toLowerCase(), source: lead.source || quelle } as CreateLeadInput);
  });

  return { leads, zeilen: zeilen.length - 1, ohneEmail, spalten: kopf, unbekannt, trennzeichen };
}
